import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import cx from 'classnames';
import useOutsideClick from '../../../hook/useOutsideClick';
import { updateTaskFolderService } from '../../../services/tasks';
import { DND_TYPE_TASK, DND_TYPE_TASK_FOLDER, DND_TYPE_TASK_FORM, getDropTargetFolderId } from '../constants';

const TaskFolderItem = ({ folder, hasWritePermissions, changeFolder, handleEdit, handleDelete, handleMove, handleMoveError }) => {
  const menuButtonWrapperRef = useRef(null);
  const menuRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, right: 'initial' });

  useOutsideClick(menuButtonWrapperRef, () => setIsOpen(false));

  const [{ isOver }, drop] = useDrop(() => ({
    accept: [DND_TYPE_TASK, DND_TYPE_TASK_FOLDER, DND_TYPE_TASK_FORM],
    drop: () => ({ item: folder }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [, drag] = useDrag(
    () => ({
      type: DND_TYPE_TASK_FOLDER,
      item: { item: folder },
      canDrag: hasWritePermissions,
      end: (draggedItem, monitor) => {
        const dropResult = monitor.getDropResult();

        if (!dropResult?.item) {
          return;
        }

        const targetFolderId = getDropTargetFolderId(dropResult.item);

        // Dropping a folder on itself or back where it already sits is not a move.
        if (targetFolderId === draggedItem.item.id || targetFolderId === (draggedItem.item.parent_id ?? null)) {
          return;
        }

        updateTaskFolderService({ parent_id: targetFolderId }, draggedItem.item.id)
          .then(() => handleMove(targetFolderId))
          .catch((e) => {
            console.log(e);
            handleMoveError(true);
          });
      },
    }),
    [folder, hasWritePermissions, handleMove, handleMoveError],
  );

  useEffect(() => {
    const menuButton = menuButtonWrapperRef.current;
    if (menuButton && isOpen && menuRef.current) {
      const menuButtonRight = menuButton.getBoundingClientRect().right;

      // 300 - menu width
      if (menuButtonRight + 300 > window.innerWidth) {
        setMenuPosition({ left: 'initial', right: 0 });
      } else {
        setMenuPosition({ left: 0, right: 'initial' });
      }
    }
  }, [isOpen]);

  return (
    <div
      ref={drag}
      className={cx('library-item list-item cursor-pointer library-folder tasks-row tasks-folder', { 'tasks-folder-over': isOver })}
      onClick={() => changeFolder(folder.id)}
    >
      <div ref={drop} className="library-item-text tasks-row-main">
        <img className="tasks-row-icon" src="/images/folder-big-icon.png" alt="" />
        <span className="tasks-row-name">{folder.name}</span>
      </div>
      {hasWritePermissions && (
        <div ref={menuButtonWrapperRef} className={cx('library-item-button', { 'library-item-button-open': isOpen })}>
          <button
            className="btn btn-outline btn-small library-item-menu-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
          >
            <img src="/images/more-vertical.svg" alt="" />
          </button>
          {isOpen && (
            <div className="library-item-menu" ref={menuRef} style={{ left: menuPosition.left, right: menuPosition.right }}>
              <button
                className="btn btn-full btn-dark"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  handleEdit(folder);
                }}
              >
                <i className="icon-icon-edit mr-2" />
                Rename
              </button>
              <button
                className="btn btn-full btn-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  handleDelete(folder);
                }}
              >
                <i className="icon-remove mr-2" />
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskFolderItem;
